import React from 'react';
import { useTranslate } from '../../../components/TranslationText';
import EahsForm from '../../../components/EahsForm';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormTextInput, FormDateInput } from '../../../components/FormInputs';

/**
 * Racing Results section — self-contained table with add/delete rows.
 * Uses FormTextInput / FormDateInput for consistency with the rest of the form.
 *
 * Each row: { raceName, classification, raceDate, rank }
 *
 * @param {boolean} isBestHorse - If true, fields show (required) labels
 * @param {boolean} disabled    - Disable all inputs (view-only mode)
 */
export default function SemenRacingResults({
    disabled = false,
    isBestHorse = false,
}) {
    const { translate } = useTranslate();
    const { control, formState: { errors } } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'racingResults',
    });

    // Array-level error (e.g. "all rows must be filled when isBestHorse")
    const globalError = errors?.racingResults?.root?.message || errors?.racingResults?.message;

    const today = new Date().toISOString().split('T')[0];

    const handleAddRow = () => {
        append({ raceName: '', classification: '', raceDate: '', rank: '' });
    };

    const handleDeleteRow = (index) => {
        if (fields.length <= 1) return;
        remove(index);
    };

    return (
        <EahsForm
            id="racing-results-table"
            key="racingResults"
            title={translate('web.mareRacingResults', 'Mare Racing Results')}
        >
            <div className="scrolableTable mx-auto w-100 border-15">
                <table
                    className="table table-striped table-bordered rtl horseResultTable border-15"
                    style={{ overflow: 'hidden' }}
                >
                    <thead className="theadHorseSelect">
                        <tr className="text-center">
                            <th>{translate('web.raceName', 'Race Name')} {isBestHorse && <span className="text-danger">*</span>}</th>
                            <th>{translate('web.raceClassification', 'Race Classification')} {isBestHorse && <span className="text-danger">*</span>}</th>
                            <th>{translate('web.raceDate', 'Race Date')} {isBestHorse && <span className="text-danger">*</span>}</th>
                            <th>{translate('web.rank', 'Rank')} {isBestHorse && <span className="text-danger">*</span>}</th>
                            <th className="text-center">{translate('web.action', 'Action')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((field, index) => (
                            <tr key={field.id}>
                                <td>
                                    <FormTextInput
                                        name={`racingResults.${index}.raceName`}
                                        placeholder={translate('web.raceName', 'Race Name')}
                                        disabled={disabled}
                                        hideLabel
                                        noMargin
                                        hideError
                                    />
                                </td>
                                <td>
                                    <FormTextInput
                                        name={`racingResults.${index}.classification`}
                                        placeholder={translate('web.raceClassification', 'Race Classification')}
                                        disabled={disabled}
                                        hideLabel
                                        noMargin
                                        hideError
                                    />
                                </td>
                                <td>
                                    <FormDateInput
                                        name={`racingResults.${index}.raceDate`}
                                        disabled={disabled}
                                        hideLabel
                                        noMargin
                                        hideError
                                        minDate="1900-01-01"
                                        maxDate={today}
                                    />
                                </td>
                                <td>
                                    <FormTextInput
                                        name={`racingResults.${index}.rank`}
                                        type="number"
                                        inputMode="numeric"
                                        placeholder={translate('web.rank', 'Rank')}
                                        disabled={disabled}
                                        hideLabel
                                        noMargin
                                        rules={{
                                            pattern: { value: /^[0-9]*$/, message: '' },
                                            min: { value: 1, message: '' },
                                        }}
                                    />
                                </td>
                                <td className="text-center">
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                        {fields.length > 1 && (
                                            <div
                                                className="btn btn-danger w-unset text-center border-15"
                                                onClick={() => !disabled && handleDeleteRow(index)}
                                                title={translate('web.delete', 'Delete')}
                                                style={{
                                                    opacity: disabled ? 0.65 : 1,
                                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                                }}
                                            >
                                                <i className="fa fa-trash w-unset" aria-hidden="true" />
                                            </div>
                                        )}
                                        {index === fields.length - 1 && !disabled && (
                                            <div
                                                className="btn btn-dark w-unset text-center border-15"
                                                onClick={handleAddRow}
                                                title={translate('web.add', 'Add')}
                                            >
                                                <i className="fa fa-plus w-unset" aria-hidden="true" />
                                            </div>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {globalError && typeof globalError === 'string' && (
                <div className="text-danger mt-2" style={{ fontSize: 13, marginLeft: 15 }}>
                    {globalError}
                </div>
            )}
        </EahsForm>
    );
}
